import { Paper, styled, Typography } from '@mui/material'
import { z } from 'zod'
import ReturnsAnnexure from '../components/Returns/ReturnsStep/components/ReturnsAnnexure'
import ReturnsChallan from '../components/Returns/ReturnsStep/components/ReturnsChallan'
import ReturnsForm from '../components/Returns/ReturnsStep/components/ReturnsForm'
import { WhiteTooltip } from './prepare'
import { getDisabledFields } from './tanFormDisabled'
import {
  BOOK_ENTRY_DEPOSIT_OPTIONS,
  MINOR_HEAD_CHALLAN_DATA,
} from './allocateColumn'
import { COUNTRY_LIST, GROSSING_UP_OPTIONS, NATURE_OF_REMITTANCE_OPTIONS, TDS_RATE_BELONGS_TO_OPTIONS } from '.'
import { numericUpTo10DigitsOptionalField, numericUpTo10DigitsRequiredField, emailValidationOptional, emailValidationRequired } from '@/utils/validation'
import { ddMMYYYYWithSlashOptional, isZeroOrEmptyNumeric } from './validationHelpers'

const getEnumValues = (options) => options.map((opt) => opt.value);

// Form type constants
export const FORM_26Q = '26Q'
export const FORM_27Q = '27Q'
export const FORM_140 = '140'
export const FORM_144 = '144'

export const GENERATE_FVU_STYLED_PAPER = styled(Paper)(() => ({
  border: '2px dashed #AAAAAA',
  borderRadius: '8px',
  padding: '24px 32px',
  backgroundColor: '#ebebeb',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#999',
    backgroundColor: '#f5f5f5',
  },
  '&.dragover': {
    borderColor: '#ebebeb',
    backgroundColor: '#ebebeb',
  },
  display: 'flex',
  justifyContent: 'center',
  gap: 4,
}))

export const RETURN_STEP_TABS = [
  {
    label: 'Form',
    content: ReturnsForm,
  },
  { label: 'Challan', content: ReturnsChallan },
  {
    label: 'Annexure I (Deductee Details)',
    content: ReturnsAnnexure,
  },
]

const numeric12_2 = z
  .string()
  .regex(
    /^\d{0,12}(\.\d{0,2})?$/,
    'Must be a number up to 12 digits and 2 decimals'
  )
  .transform(val => (val === '' ? '0' : val))

// Mandatory numeric fields (12 digits, 2 decimals), default to "0" if empty
const mandatoryNumeric12_2 = z
  .string()
  .nonempty('This field is required')
  .regex(
    /^\d{0,12}(\.\d{0,2})?$/,
    'Must be a number up to 12 digits and 2 decimals'
  )
  .transform(val => (val === '' ? '0' : val))

const ddMMYYYYWithSlash = z
  .string()
  .regex(
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
    'Invalid format. Example: 21/10/2024 (dd/mm/yyyy)'
  )

export const returnsChallanRowSchema = z.object({
  challanSerialNo: z
    .string()
    .nonempty({ message: 'This field is required in TDS return.' })
    .regex(/^\d{1,9}$/, { message: 'Must be numeric and up to 9 digits.' }),
  bsrCode: z
    .string()
    .nonempty({ message: 'This field is required in TDS return.' })
    .regex(/^\d{7}$/, { message: 'Should contain exactly 7 digits.' }),
  dateOfTransferVoucher: z
    .string()
    .nonempty({ message: 'This field is required in TDS return.' })
    .regex(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, {
      message: 'Date format should be DD/MM/YYYY',
    }),
  totalAmountAsPerChallan: mandatoryNumeric12_2,
  surcharge: numeric12_2,
  educationCess: numeric12_2,
  interest: numeric12_2,
  fee: numeric12_2,
  penalty: numeric12_2,
  others: numeric12_2,
  tds: numeric12_2,
  sectionCode: z
    .string()
    .nonempty({ message: 'Section as per Challan is mandatory' }),
  interestAllocated: numeric12_2,  
})

export const returnsAnnexureRowSchema = z
  .object({
    challanSerialNo: z
      .string()
      .nonempty({ message: 'Challan Serial No. is required.' })
      .regex(/^\d{1,9}$/, {
        message: 'Must be numeric and up to 9 digits.',
      }),

    sectionCode: z.string().nonempty({ message: 'Section Code is required.' }),

    deducteeReferenceNo: z.string().optional(),

    panOfDeductee: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),

    deducteeName: z
      .string()
      .nonempty({ message: 'Deductee Name is required.' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),

    dateOfPaymentOrCredit: ddMMYYYYWithSlash,

    amountPaidOrCredited: mandatoryNumeric12_2,
    totalTaxDeposited: mandatoryNumeric12_2,

    tds: mandatoryNumeric12_2,

    surcharge: numeric12_2,

    healthAndEducationCess: numeric12_2,

    // Conditionally required: enforced via superRefine based on totalTaxDeducted.
    dateOfDeduction: ddMMYYYYWithSlashOptional,

    formType: z.string(), // <-- ADD THIS FIELD IN YOUR DATA OBJECT

    natureOfRemittance: z
      .string()
      .optional().nullable(),

    vendorEmailId: emailValidationOptional('Vendor Email ID'),

    vendorContactNumber: z
      .string()
      .trim()
      .transform(val => (val === '' ? undefined : val))
      .optional()
      .nullable()
      .refine(val => !val || /^[0-9]{10,15}$/.test(val), {
        message: 'Contact number must be 10ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“15 digits',
      }),

    remarks: z
      .string()
      .optional()
      .nullable()
      .refine(val => !val || /^[A-Za-z]$/.test(val), {
        message: 'Invalid Remarks',
      }),

    deducteeCode: z
      .string()
      .nonempty({ message: 'Deductee Code is required.' })
      .refine(val => val === '1' || val === '2', {
        message: 'Invalid Deductee Code.',
      }),

    rateOfTaxDeducted: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, {
      message: 'Must be a number up to 3 digits and 2 decimals',
    }),

    certificateNo197: z
      .string()
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    // Nature of Remittance is mandatory for Form 27Q.
    if (
      data.formType === '27Q' &&
      (!data.natureOfRemittance || data.natureOfRemittance.trim() === '')
    ) {
      ctx.addIssue({
        path: ['natureOfRemittance'],
        code: z.ZodIssueCode.custom,
        message: 'Nature Of Remittance is required for Form 27Q.',
      })
    }

    // Date of deduction is required only when tax has actually been deducted.
    // When Total Tax Deducted is zero/undefined/null, it stays optional.
    if (!isZeroOrEmptyNumeric(data.totalTaxDeducted) && !data.dateOfDeduction) {
      ctx.addIssue({
        path: ['dateOfDeduction'],
        code: z.ZodIssueCode.custom,
        message: 'Date of Deduction is required.',
      })
    }
  });


export const RETURNS_CHALLAN_COLUMNS = [
  {
    id: 'serialNo',
    label: 'Serial no.',
    width: '150px',
  },
  {
    id: 'updatedModeOfChallan',
    label: 'Update mode of challan',
    width: '250px',
  },
  {
    id: 'sectionCode',
    label: 'Section code',
    width: '160px',
    editable: true,
    isFormatted: false
  },
  {
    id: 'tds',
    label: 'TDS',
    width: '150px',
    editable: true,
  },
  {
    id: 'surcharge',
    label: 'Surcharge',
    width: '150px',
    editable: true,
  },
  {
    id: 'educationCess',
    label: 'Education cess',
    width: '160px',
    editable: true,
  },
  {
    id: 'interest',
    label: 'Interest',
    width: '150px',
    editable: true,
  },
  {
    id: 'fee',
    label: 'Fee',
    width: '150px',
    editable: true,
  },
  {
    id: 'penalty',
    label: 'Penalty/others',
    width: '150px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastTotalTaxDeposited',
    label: 'Last total tax deposited',
    width: '160px',
    align: 'center',
  },
  {
    id: 'totalAmountAsPerChallan',
    label: 'Total amount as per challan/ book adjustment',
    width: '240px',
    align: 'center',
  },
  {
    id: 'chequeNo',
    label: 'Cheque/ DD no. (if any)',
    width: '160px',
    isFormatted: false
  },
  {
    id: 'lastBsrCode',
    label: 'Last bsr code/ 24G receipt no.',
    width: '160px',
    isFormatted: false
  },
  {
    id: 'bsrCode',
    label: 'BSR code/ receipt number of form no. 24G',
    width: '250px',
    align: 'center',
    editable: true,
    isFormatted: false
  },
  {
    id: 'lastDateOfTaxDeposited',
    label: 'Last date on which tax deposited',
    width: '250px',
    align: 'center',
  },
  {
    id: 'dateOfTransferVoucher',
    label:
      'Date on which amount deposited through challan/ date of transfer voucher (DD/MM/YYYY)',
    width: '350px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastChallanSerialNo',
    label: 'Last DDO/ transfer voucher/ challan serial no.',
    width: '300px',
    align: 'center',
    isFormatted: false
  },
  {
    id: 'challanSerialNo',
    label: 'Challan serial no./ DDO serial no. of form no. 24G',
    width: '300px',
    align: 'center',
    editable: true,
    isFormatted: false
  },
  {
    id: 'modeOfDepositThroughBookAdjustment',
    label: 'Mode of deposit through book adjustment (Yes/No)',
    width: '300px',
    editable: true,
    type: 'dropdown',
    options: BOOK_ENTRY_DEPOSIT_OPTIONS,
    align: 'center',
  },
  {
    id: 'interestAllocated',
    label: 'Interest to be allocated/apportioned',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'others',
    label: 'Others',
    width: '150px',
    editable: true,
  },
  {
    id: 'minorHeadOfChallan',
    label:
      'Minor head of challan 200- TDS payable by taxpayer 400- TDS regular assessment (raised by IT dept)',
    width: '350px',
    editable: true,
    type: 'dropdown',
    options: MINOR_HEAD_CHALLAN_DATA,
    align: 'center',
    isFormatted: false
  },
  {
    id: 'challanBalanceAsPerConsolidatedFile',
    label: 'Challan Balance as per Consolidated file',
    width: '250px',
    align: 'center',
  },
]

export const RETURNS_ANNEXURE_COLUMNS = [
  {
    id: 'serialNo',
    label: 'Serial no.',
    width: '150px',
  },
  {
    id: 'challanSerialNo',
    label: 'Challan serial no.',
    width: '150px',
    editable: true,
    isFormatted: false
  },
  {
    id: 'updateMode',
    label: 'Update mode for deductee (Add/ Update/ PAN Update)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'bsrCode',
    label: 'BSR code of branch where tax deposited',
    width: '250px',
    align: 'center',
    isFormatted: false
  },
  {
    id: 'dateOfTaxDeposited',
    label: 'Date on which tax deposited (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'transferVoucherOrChallanSerialNo',
    label: 'Transfer voucher/ challan serial no.',
    width: '200px',
    align: 'center',
    isFormatted: false
  },
  {
    id: 'sectionCode',
    label: 'Section under which payment made',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'totalTdsToBeAllocated',
    label: 'Total TDS to be allocated among deductees',
    width: '250px',
    align: 'center',
  },
  {
    id: 'interest',
    label: 'Interest',
    width: '150px',
  },
  {
    id: 'others',
    label: 'Others',
    width: '150px',
  },
  {
    id: 'total',
    label: 'Total',
    width: '150px',
  },
  {
    id: 'deducteeSerialNo',
    label: 'S. no.',
    width: '150px',
  },
  {
    id: 'deducteeReferenceNo',
    label: 'Deductee reference no. provided by the deductor (if available)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastPanOfDeductee',
    label: 'Last PAN of deductee',
    width: '200px',
  },
  {
    id: 'panOfDeductee',
    label: 'PAN of the deductee',
    width: '160px',
    editable: true,
  },
  {
    id: 'deducteeName',
    label: 'Name of the deductee',
    width: '240px',
    editable: true,
  },
  {
    id: 'dateOfPaymentOrCredit',
    label: 'Date of payment/ credit (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'amountPaidOrCredited',
    label: 'Amount of payment/ credited',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'tds',
    label: 'TDS',
    width: '150px',
    editable: true,
  },
  {
    id: 'surcharge',
    label: 'Surcharge',
    width: '150px',
    editable: true,
  },
  {
    id: 'healthAndEducationCess',
    label: 'Health and education cess',
    width: '180px',
    align: 'center',
    editable: true,
  },
  {
    id: 'totalTaxDeducted',
    label: 'Total tax deducted',
    width: '160px',
    align: 'center',
  },
  {
    id: 'lastTotalTaxDeducted',
    label: 'Last total tax deducted',
    width: '180px',
    align: 'center',
  },
  {
    id: 'totalTaxDeposited',
    label: 'Total tax deposited',
    width: '160px',
    align: 'center',
    editable: true
  },
  {
    id: 'lastTotalTaxDeposited',
    label: 'Last total tax deposited',
    width: '240px',
  },
  {
    id: 'dateOfDeduction',
    label: 'Date of deduction (DD/MM/YYYY)',
    width: '240px',
    align: 'center',
    editable: true,
  },
  {
    id: 'remarks',
    label:
      'Remarks (Reason for non-deduction/ lower deduction/ higher deduction/ threshold)',
    width: '300px',
    editable: true,
  },
  {
    id: 'deducteeCode',
    label: 'Deductee code',
    width: '150px',
    editable: true,
  },
  {
    id: 'rateOfTaxDeducted',
    label: 'Rate at which tax deducted',
    width: '160px',
    editable: true,
  },
  {
    id: 'paidByBookEntry',
    label: 'Paid by book entry or otherwise',
    width: '160px',
  },
  {
    id: 'certificateNo197',
    label:
      'Certificate number issued by the assessing officer u/s 197 for non-deduction/ lower deduction',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'cashWithdrawalExceeding1Cr',
    label:
      'Amount of cash withdrawal in excess of Rs. 1 crore as referred to in section 194N (in cases not covered by the first proviso to section 194N)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawal20LTo1Cr',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 20 lakh but does not exceed Rs. 1 crore for cases covered by sub-clause (a) of clause (ii) of first proviso to section 194N',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawalExceeding1CrProvisoB',
    label:
      'Amount of cash withdrawal in excess of Rs. 1 crore for cases covered by sub-clause (b) of clause (ii) of first proviso to section 194N',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawalExceeding3CrCooperative',
    label:
      'Amount of cash withdrawal in excess of Rs. 3 crore in case of co-operative societies as covered by third proviso to section 194N (in cases not covered by the first proviso to section 194N)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawal20LTo3CrCooperative',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 20 lakh but does not exceed Rs. 3 crore for cases covered by sub-clause (a) of clause (ii) of first proviso read with third proviso to section 194N (in case of co-operative societies)',
    width: '250px',
  },
  {
    id: 'cashWithdrawalExceeding3CrProvisoBCooperative',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 3 crore for cases covered by sub-clause (b) of clause (ii) of first proviso read with third proviso to section 194N (in case of co-operative societies)',
    width: '250px',
    align: 'center',
  },
]

export const RETURNS_ANNEXURE_COLUMNS_27Q = [
  {
    id: 'serialNo',
    label: 'Serial no.',
    width: '150px',
  },
  {
    id: 'challanSerialNo',
    label: 'Challan serial no.',
    width: '150px',
    editable: true,
    isFormatted: false
  },
  {
    id: 'updateMode',
    label: 'Update mode for deductee (Add/ Update/ PAN Update)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'bsrCode',
    label: 'BSR code of branch where tax deposited',
    width: '250px',
    align: 'center',
    isFormatted: false
  },
  {
    id: 'dateOfTaxDeposited',
    label: 'Date on which tax deposited (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'transferVoucherOrChallanSerialNo',
    label: 'Transfer voucher/ challan serial no.',
    width: '200px',
    align: 'center',
    isFormatted: false
  },
  {
    id: 'sectionCode',
    label: 'Section under which payment made',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'totalTdsToBeAllocated',
    label: 'Total TDS to be allocated among deductees',
    width: '250px',
    align: 'center',
  },
  {
    id: 'interest',
    label: 'Interest',
    width: '150px',
  },
  {
    id: 'others',
    label: 'Others',
    width: '150px',
  },
  {
    id: 'total',
    label: 'Total',
    width: '150px',
  },
  {
    id: 'deducteeSerialNo',
    label: 'S. no.',
    width: '150px',
  },
  {
    id: 'deducteeReferenceNo',
    label: 'Deductee reference no. provided by the deductor (if available)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastPanOfDeductee',
    label: 'Last PAN of deductee',
    width: '200px',
  },
  {
    id: 'panOfDeductee',
    label: 'PAN of the deductee',
    width: '160px',
    editable: true,
  },
  {
    id: 'deducteeName',
    label: 'Name of the deductee',
    width: '240px',
    editable: true,
  },
  {
    id: 'dateOfPaymentOrCredit',
    label: 'Date of payment/ credit (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'amountPaidOrCredited',
    label: 'Amount of payment/ credited',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'tds',
    label: 'TDS',
    width: '150px',
    editable: true,
  },
  {
    id: 'surcharge',
    label: 'Surcharge',
    width: '150px',
    editable: true,
  },
  {
    id: 'healthAndEducationCess',
    label: 'Health and education cess',
    width: '180px',
    align: 'center',
    editable: true,
  },
  {
    id: 'totalTaxDeducted',
    label: 'Total tax deducted',
    width: '160px',
    align: 'center',
  },
  {
    id: 'lastTotalTaxDeducted',
    label: 'Last total tax deducted',
    width: '180px',
    align: 'center',
  },
  {
    id: 'totalTaxDeposited',
    label: 'Total tax deposited',
    width: '160px',
    align: 'center',
    editable: true
  },
  {
    id: 'lastTotalTaxDeposited',
    label: 'Last total tax deposited',
    width: '240px',
  },
  {
    id: 'dateOfDeduction',
    label: 'Date of deduction (DD/MM/YYYY)',
    width: '240px',
    align: 'center',
    editable: true,
  },
  {
    id: 'remarks',
    label:
      'Remarks (Reason for non-deduction/ lower deduction/ higher deduction/ threshold)',
    width: '300px',
    editable: true,
  },
  {
    id: 'deducteeCode',
    label: 'Deductee code',
    width: '150px',
    editable: true,
  },
  {
    id: 'rateOfTaxDeducted',
    label: 'Rate at which tax deducted',
    width: '160px',
    editable: true,
  },
  {
    id: 'paidByBookEntry',
    label: 'Paid by book entry or otherwise',
    width: '160px',
  },
  {
    id: 'certificateNo197',
    label:
      'Certificate number issued by the assessing officer u/s 197 for non-deduction/ lower deduction',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'tdsRateActOrDtaa',
    label:
      'Whether TDS rate of TDS is IT act (a) and DTAA (b)',
    width: '250px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: TDS_RATE_BELONGS_TO_OPTIONS
  },
  {
    id: 'natureOfRemittance',
    label:
      'Nature of remittance',
    width: '250px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: NATURE_OF_REMITTANCE_OPTIONS
  },
  {
    id: 'acknowledgement15CA',
    label:
      'Unique acknowledgement of the corrosponding form no 15CA (if available)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'countryOfResidence',
    label:
      'Country of residence of the deductee',
    width: '250px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: COUNTRY_LIST,
  },
  {
    id: 'grossingUpIndicator',
    label:
      'Grossing up indicator',
    width: '250px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: GROSSING_UP_OPTIONS
  },
  {
    id: 'vendorEmailId',
    label:
      'Vendor email ID',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'vendorContactNumber',
    label:
      'Vendor contact number',
    width: '250px',
    align: 'center',
    editable: true,
    isFormatted: false
  },
  {
    id: 'vendorAddress',
    label:
      'Vendor address',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'vendorTaxIdentificationNo',
    label:
      'Tax identification number /unique identification number of deductee',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'cashWithdrawalExceeding1Cr',
    label:
      'Amount of cash withdrawal in excess of Rs. 1 crore as referred to in section 194N (in cases not covered by the first proviso to section 194N)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawal20LTo1Cr',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 20 lakh but does not exceed Rs. 1 crore for cases covered by sub-clause (a) of clause (ii) of first proviso to section 194N',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawalExceeding1CrProvisoB',
    label:
      'Amount of cash withdrawal in excess of Rs. 1 crore for cases covered by sub-clause (b) of clause (ii) of first proviso to section 194N',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawalExceeding3CrCooperative',
    label:
      'Amount of cash withdrawal in excess of Rs. 3 crore in case of co-operative societies as covered by third proviso to section 194N (in cases not covered by the first proviso to section 194N)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'cashWithdrawal20LTo3CrCooperative',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 20 lakh but does not exceed Rs. 3 crore for cases covered by sub-clause (a) of clause (ii) of first proviso read with third proviso to section 194N (in case of co-operative societies)',
    width: '250px',
  },
  {
    id: 'cashWithdrawalExceeding3CrProvisoBCooperative',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 3 crore for cases covered by sub-clause (b) of clause (ii) of first proviso read with third proviso to section 194N (in case of co-operative societies)',
    width: '250px',
    align: 'center',
  },
]

export const GENERATE_FVU_COLUMNS = [
  {
    id: 'documentName',
    label: 'Document name',
    width: '150px',
  },
  {
    id: 'uploadedBy',
    label: 'Uploaded by',
    width: '150px',
  },
  {
    id: 'date',
    label: 'Date',
    width: '150px',
  },
  {
    id: 'status',
    label: 'Status',
    width: '150px',
  },

  {
    id: 'downloadFile',
    label: 'Download file',
    width: '150px',
    render: value => {
      return (
        <Typography
          component="span"
          sx={{
            color: '#CF6210',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: '400',
            fontSize: '14px',
          }}
        >
          {value}
        </Typography>
      )
    },
  },
]

export const DOWNLOAD_FILES_COLUMNS = [
  {
    id: 'quarter',
    label: 'Quarter',
    width: '100px',
  },
  {
    id: 'entityTan',
    label: 'TAN',
    width: '120px',
  },
  {
    id: 'returnType',
    label: 'Return type',
    width: '120px',
  },
  {
    id: 'formType',
    label: 'Form',
    width: '120px',
  },
  {
    id: 'returnsFileType',
    label: 'File type',
    width: '120px',
  },
  {
    id: 'createdBy',
    label: 'Generated by',
    width: '150px',
  },
  {
    id: 'lastModifiedDate',
    label: 'Generated date',
    width: '150px',
  },
  {
    id: 'status',
    label: 'Status',
    width: '150px',
    render: (value, row) => {
      const getStatusColor = status => {
        switch (status?.toUpperCase()) {
          case 'COMPLETED':
            return { backgroundColor: '#F0FDF3', color: '#197C37' }
          case 'IN PROGRESS':
            return { backgroundColor: '#FFDFC4', color: '#D17300' }
          default:
            return { backgroundColor: '#E2E3E5', color: '#495057' }
        }
      }

      const { backgroundColor, color } = getStatusColor(value)

      const badge = (
        <span
          style={{
            backgroundColor,
            color,
            padding: '2px 6px',
            borderRadius: 4,
            display: 'inline-block',
            cursor: 'pointer',
          }}
        >
          {value || 'N/A'}
        </span>
      )

      return badge
    },
  },
]

export const RETURNS_FORM_SCHEMA = z
  .object({
    // Particulars Of Statement
    tanNumber: z
      .string()
      .trim()
      .min(1, 'TAN is required')
      .max(10, 'TAN must be exactly 10 characters')
      .regex(
        /^[A-Z]{3}[A-Z]{1}[0-9]{5}[A-Z]{1}$/,
        'Invalid TAN: Format must be 3 letters + 1 letter + 5 digits + 1 letter'
      ),
    financialYear: z
      .string()
      .regex(/^\d{4}-\d{2}$/, 'Tax Year must be in format YYYY-YY'),
    lastTan: z.string().optional().nullable(),
    pan: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),
    revisedReturn: z.string().min(1, 'Revised Return is required'),
    deductorType: z.string().min(1, 'Type of Deductor is required'),
    previousReturnReceiptNo: z.string().optional().nullable(),
    lastDeductorType: z.string().optional().nullable(),
    originalReturnReceiptNo: z.string().optional().nullable(),
    quarterEnded: z.string().min(1, 'Quarter is required'),

    // Particulars Of Deductor (Employer)
    name: z.string().trim().min(1, 'Name is required').max(75, 'Name of the Responsible Person cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    buildingName: z
      .string()
      .trim()
      .max(25, 'Name of the Premises/Building cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    branchOrDivision: z.string().trim().min(1, 'Branch/Division is required')
      .max(75, 'Branch/Division cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    areaOrLocation: z
      .string()
      .trim()
      .max(25, { message: 'Area/Locality cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    flatNo: z
      .string()
      .trim()
      .min(1, 'Flat/Door/Block No is required')
      .max(25, 'Flat/Door/Block No. cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    stateName: z.string().trim().optional().nullable(),
    roadOrStreet: z
      .string()
      .trim()
      .max(25, 'Road/Street/Lane cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    pinCode: z
      .string()
      .trim()
      .refine(val => /^[0-9]{6}$/.test(val), {
        message: 'PIN Code must be numeric and should be 6 digits',
      }),
    townDistrictCity: z
      .string()
      .trim()
      .max(25, { message: 'Town/District/City cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional()
      .transform(val => val ?? ''),
    email: emailValidationOptional('Email ID'),
    stdCode: z
      .string()
      .trim()
      .max(5, 'STD Code cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD code must be numeric')
      .nullable()
      .optional(),
    telephoneNo: numericUpTo10DigitsOptionalField('Telephone No.'),
    alternateStdCode: z
      .string()
      .trim()
      .max(5, 'STD Code cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD code must be numeric')
      .nullable()
      .optional(),
    alternateTelephoneNo: numericUpTo10DigitsOptionalField('Telephone No.'),
    alternateEmail: emailValidationOptional('Email ID'),
    paoCode: z
      .string()
      .trim()
      .max(20, 'PAO Code cannot exceed 20 characters')
      .regex(/^$|^[A-Za-z0-9]{1,20}$/, 'Invalid PAO Code (alphanumeric only)')
      .nullable()
      .optional(),
    gstn: z
      .string()
      .trim()
      .max(15, 'GSTN cannot exceed 15 characters')
      .regex(
        /^$|^[A-Z0-9]{15}$/i,
        'GSTN must contain 15 alphanumeric charcaters'
      )
      .nullable()
      .optional(),
    ddoCode: z
      .string()
      .trim()
      .max(20, 'DDO Code cannot exceed 20 characters')
      .regex(/^$|^[A-Za-z0-9]{1,20}$/, 'Invalid DDO Code (alphanumeric only)')
      .optional(),
    ministryName: z.string().trim().optional().nullable(),
    paoRegistrationNo: z
      .string()
      .trim()
      .max(7, 'PAO Registration No. cannot exceed 7 characters')
      .regex(
        /^$|^[0-9]{1,7}$/,
        'Invalid PAO Registration Number (numeric only)'
      )
      .nullable()
      .optional(),
    ministryNameOther: z
      .string()
      .trim()
      .regex(
        /^$|^[A-Za-z0-9 ]+$/,
        'Invalid Ministry Name Other (alphanumeric only)'
      )
      .optional()
      .nullable(),
    ddoRegistrationNo: z
      .string()
      .trim()
      .max(20, 'DDO Registration Number cannot exceed 20 characters')
      .regex(
        /^$|^[A-Za-z0-9]{1,20}$/,
        'Invalid DDO Registration Number (alphanumeric only)'
      )
      .optional(),
    state: z.string().min(1, 'State is required'),
    addressChangedSinceLastReturn: z.enum(['Yes', 'No'], {
      errorMap: () => ({
        message: 'Has Address changed since Last Return is required',
      }),
    }),

    accountOfficeIdNumber: z
      .string()
      .max(75, 'AIN cannot exceed 75 characters')
      .optional(),
    // Particulars Of The Person Responsible For Deduction Of Tax
    responsiblePersonName: z.string().min(1, 'Name is required').max(75, 'Name of the Responsible Person cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    responsiblePersonPAN: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),
    responsiblePersonBuilding: z
      .string()
      .trim()
      .max(25, 'Name of the Premises/Building cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    responsiblePersonDesignation: z.string().trim().min(1, 'Designation is Required.').max(20, { message: 'Designation cannot exceed 20 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    responsiblePersonArea: z
      .string()
      .trim()
      .max(25, { message: 'Area/Locality cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    responsiblePersonFlat: z
      .string()
      .trim()
      .min(1, 'Flat/Door/Block No is Required')
      .max(25, 'Flat/Door/Block No. cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    responsiblePersonState: z.string().min(1, 'State is required'),
    responsiblePersonStreet: z
      .string()
      .trim()
      .max(25, 'Road/Street/Lane cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    responsiblePersonPIN: z
      .string()
      .trim()
      .refine(val => /^[0-9]{6}$/.test(val), {
        message: 'PIN Code must be numeric and should be 6 digits',
      }),
    responsiblePersonCity: z
      .string()
      .trim()
      .max(25, { message: 'Town/District/City cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional()
      .transform(val => val ?? ''),
    responsiblePersonEmail: emailValidationOptional('Email ID'),
    responsiblePersonEmailAlt: emailValidationOptional('Email ID'),
    responsiblePersonSTD: z
      .string()
      .trim()
      .max(5, 'STD Code cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD code must be numeric')
      .nullable()
      .optional(),
    responsiblePersonTelephone: numericUpTo10DigitsOptionalField('Telephone No.'),
    responsiblePersonSTDAlt: z
      .string()
      .trim()
      .max(5, 'STD Code cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD code must be numeric')
      .nullable()
      .optional(),
    responsiblePersonTelephoneAlt: numericUpTo10DigitsOptionalField('Telephone No.'),
    responsiblePersonAddressChanged: z.enum(['Yes', 'No'], {
      errorMap: () => ({
        message: 'Has Address changed since Last Return is required',
      }),
    }),
    responsiblePersonMobile: numericUpTo10DigitsRequiredField('Mobile No.'),

    responsiblePersonRegularStatement: z.enum(['Yes', 'No'], {
      errorMap: () => ({
        message:
          'Has regular statement filed for Form 26Q for ealier period is required',
      }),
    }),
    responsiblePersonReceiptNumber: z
      .string()
      .trim()
      .optional()
      .refine(val => !val || /^[0-9]+$/.test(val), {
        message: 'Receipt No. must contain only numeric values',
      })
      .refine(val => !val || val.length <= 15, {
        message: 'Receipt No. must not exceed 15 digits',
      }),
  })
  .superRefine((data, ctx) => {
    const disabled = getDisabledFields(data.deductorType)

    // PAN validation - Entity PAN and Responsible Person PAN should not be same
    if (data.pan && data.pan === data.responsiblePersonPAN) {
      ctx.addIssue({
        path: ['pan'],
        code: z.ZodIssueCode.custom,
        message: 'PAN of deductor should not be same as responsible person PAN',
      })
    }

    // Telephone validation - Either both STD and telephone should be filled or both should be empty
    const hasStdCode = data.stdCode && data.stdCode.trim() !== ''
    const hasTelephoneNumber =
      data.telephoneNo && data.telephoneNo.trim() !== ''

    if (
      (hasStdCode && !hasTelephoneNumber) ||
      (!hasStdCode && hasTelephoneNumber)
    ) {
      ctx.addIssue({
        path: ['telephoneNo'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both STD code & Telephone No should be blank or both should be filled',
      })
    }

    // Alternate Telephone validation
    const hasStdCodeAlt =
      data.alternateStdCode && data.alternateStdCode.trim() !== ''
    const hasTelephoneNumberAlt =
      data.alternateTelephoneNo && data.alternateTelephoneNo.trim() !== ''

    if (
      (hasStdCodeAlt && !hasTelephoneNumberAlt) ||
      (!hasStdCodeAlt && hasTelephoneNumberAlt)
    ) {
      ctx.addIssue({
        path: ['alternateTelephoneNo'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both STD code alternate & Telephone No alternate should be blank or both should be filled',
      })
    }

    // Responsible Person Telephone validation
    const hasStdCodeRP =
      data.responsiblePersonSTD && data.responsiblePersonSTD.trim() !== ''
    const hasTelephoneNumberRP =
      data.responsiblePersonTelephone &&
      data.responsiblePersonTelephone.trim() !== ''

    if (
      (hasStdCodeRP && !hasTelephoneNumberRP) ||
      (!hasStdCodeRP && hasTelephoneNumberRP)
    ) {
      ctx.addIssue({
        path: ['responsiblePersonTelephone'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both STD code & Telephone No should be blank or both should be filled',
      })
    }

    // Responsible Person Alternate Telephone validation
    const hasStdCodeRPAlt =
      data.responsiblePersonSTDAlt && data.responsiblePersonSTDAlt.trim() !== ''
    const hasTelephoneNumberRPAlt =
      data.responsiblePersonTelephoneAlt &&
      data.responsiblePersonTelephoneAlt.trim() !== ''

    if (
      (hasStdCodeRPAlt && !hasTelephoneNumberRPAlt) ||
      (!hasStdCodeRPAlt && hasTelephoneNumberRPAlt)
    ) {
      ctx.addIssue({
        path: ['responsiblePersonTelephoneAlt'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both STD code alternate & Telephone No alternate should be blank or both should be filled',
      })
    }

    // Ministry Name Other validation - Required if ministryName is 'OTHERS'
    if (
      data.ministryName === 'OTHERS' &&
      (!data.ministryNameOther || data.ministryNameOther.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ministryNameOther'],
        code: z.ZodIssueCode.custom,
        message:
          'Ministry Name Other is required when Others is selected as Ministry Name',
      })
    }

    if (
      !disabled.stateName &&
      (!data.stateName || data.stateName.trim() === '')
    ) {
      ctx.addIssue({
        path: ['stateName'],
        code: z.ZodIssueCode.custom,
        message: 'State/Ministry field is required for this entity type',
      })
    }


    // PAO Code required only if not disabled
    if (
      !disabled.paoCode &&
      data.deductorType === 'CENTRAL GOVERNMENT' &&
      (!data.paoCode || data.paoCode.trim() === '')
    ) {
      ctx.addIssue({
        path: ['paoCode'],
        code: z.ZodIssueCode.custom,
        message: 'PAO Code is required for this entity type',
      })
    }

    // DDO Code required only if not disabled
    if (
      !disabled.ddoCode &&
      data.deductorType === 'CENTRAL GOVERNMENT' &&
      (!data.ddoCode || data.ddoCode.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ddoCode'],
        code: z.ZodIssueCode.custom,
        message: 'DDO Code is required for this entity type',
      })
    }

    // Ministry Name required for Central Government and other ministry-requiring entity types
    if (
      !disabled.ministryName &&
      (!data.ministryName || data.ministryName.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ministryName'],
        code: z.ZodIssueCode.custom,
        message: 'Ministry Name is required for this entity type',
      })
    }

    // Responsible Person Regular Statement ÃƒÂ¢Ã¢â‚¬Â Ã¢â‚¬â„¢ Receipt No validation
    if (data.responsiblePersonRegularStatement === 'Yes') {
      if (
        !data.responsiblePersonReceiptNumber ||
        data.responsiblePersonReceiptNumber.trim() === ''
      ) {
        ctx.addIssue({
          path: ['responsiblePersonReceiptNumber'],
          code: z.ZodIssueCode.custom,
          message:
            'Receipt No. of earlier statement filed for Form 26Q is required',
        })
      }
    }
  })

/**
 * Form validation schema for new forms (140/144) - FY 2026-27 onwards.
 * Fields removed from the Returns form are made optional/nullable since they
 * won't be displayed or submitted.
 */
export const RETURNS_FORM_SCHEMA_NEW = z
  .object({
    // Particulars Of Statement
    tanNumber: z
      .string()
      .trim()
      .min(1, 'TAN is required')
      .max(10, 'TAN must be exactly 10 characters')
      .regex(
        /^[A-Z]{3}[A-Z]{1}[0-9]{5}[A-Z]{1}$/,
        'Invalid TAN: Format must be 3 letters + 1 letter + 5 digits + 1 letter'
      ),
    financialYear: z
      .string()
      .regex(/^\d{4}-\d{2}$/, 'Tax Year must be in format YYYY-YY'),
    lastTan: z.string().optional().nullable(),
    pan: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),
    // Removed from Return - made optional
    revisedReturn: z.string().optional().nullable(),
    deductorType: z.string().min(1, 'Type of Deductor is required'),
    previousReturnReceiptNo: z.string().optional().nullable(),
    lastDeductorType: z.string().optional().nullable(),
    originalReturnReceiptNo: z.string().optional().nullable(),
    quarterEnded: z.string().min(1, 'Quarter is required'),

    // Particulars Of Deductor (Employer)
    name: z.string().trim().min(1, 'Name is required').max(75, 'Name cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    // Removed from Return - made optional
    branchOrDivision: z.string().optional().nullable(),
    // New field: Country/Region (mandatory)
    country: z.string().trim().min(1, 'Country/Region is required').optional().nullable(),
    buildingName: z
      .string()
      .trim()
      .max(25, 'Post Office cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    areaOrLocation: z
      .string()
      .trim()
      .max(25, { message: 'Area/locality cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    flatNo: z
      .string()
      .trim()
      .min(1, 'Flat/Door/Block No is required')
      .max(25, 'Flat/Door/Block No. cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    stateName: z.string().trim().optional().nullable(),
    roadOrStreet: z
      .string()
      .trim()
      .max(25, 'Road/Street/Block/Sector cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    pinCode: z
      .string()
      .trim()
      .refine(val => /^[0-9]{6}$/.test(val), {
        message: 'PIN/ZIP Code must be numeric and should be 6 digits',
      }),
    townDistrictCity: z
      .string()
      .trim()
      .max(25, { message: 'District cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional()
      .transform(val => val ?? ''),
    email: emailValidationRequired('Email ID'),
    stdCode: z
      .string({ required_error: 'Country Code is required' })
      .trim()
      .min(1, 'Country Code is required')
      .max(5, 'Country Code cannot exceed 5 characters')
      .regex(/^[0-9]{1,5}$/, 'Country Code must be numeric'),
    telephoneNo: numericUpTo10DigitsRequiredField('Number'),
    // Removed from Return - made optional
    alternateStdCode: z.string().optional().nullable(),
    alternateTelephoneNo: z.string().optional().nullable(),
    alternateEmail: z.string().optional().nullable(),
    paoCode: z.string().optional().nullable(),
    ddoCode: z.string().optional().nullable(),
    paoRegistrationNo: z.string().optional().nullable(),
    ddoRegistrationNo: z.string().optional().nullable(),
    gstn: z
      .string()
      .trim()
      .max(15, 'GSTN cannot exceed 15 characters')
      .regex(
        /^$|^[A-Z0-9]{15}$/i,
        'GSTN must contain 15 alphanumeric characters'
      )
      .nullable()
      .optional(),
    ministryName: z.string().trim().optional().nullable(),
    ministryNameOther: z.string().trim().optional().nullable(),
    state: z.string().min(1, 'State is required'),
    // Removed from Return - made optional
    addressChangedSinceLastReturn: z.string().optional().nullable(),

    accountOfficeIdNumber: z
      .string()
      .max(75, 'AIN cannot exceed 75 characters')
      .optional(),

    // Particulars Of The Person Responsible For Deduction Of Tax
    responsiblePersonName: z.string().min(1, 'Name is required').max(75, 'Name cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    responsiblePersonPAN: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),
    responsiblePersonBuilding: z
      .string()
      .trim()
      .max(25, 'Post Office cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    responsiblePersonDesignation: z.string().trim().min(1, 'Designation is Required.').max(20, { message: 'Designation cannot exceed 20 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    responsiblePersonArea: z
      .string()
      .trim()
      .max(25, { message: 'Area/locality cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    responsiblePersonFlat: z
      .string()
      .trim()
      .min(1, 'Flat/Door/Block No is Required')
      .max(25, 'Flat/Door/Block No. cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    responsiblePersonState: z.string().min(1, 'State is required'),
    responsiblePersonStreet: z
      .string()
      .trim()
      .max(25, 'Road/Street/Block/Sector cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    responsiblePersonPIN: z
      .string()
      .trim()
      .refine(val => /^[0-9]{6}$/.test(val), {
        message: 'PIN/ZIP Code must be numeric and should be 6 digits',
      }),
    responsiblePersonCity: z
      .string()
      .trim()
      .max(25, { message: 'District cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional()
      .transform(val => val ?? ''),
    responsiblePersonEmail: emailValidationRequired('Email ID'),
    // Removed from Return - made optional
    responsiblePersonEmailAlt: z.string().optional().nullable(),
    responsiblePersonSTD: z
      .string({ required_error: 'Country Code is required' })
      .trim()
      .min(1, 'Country Code is required')
      .max(5, 'Country Code cannot exceed 5 characters')
      .regex(/^[0-9]{1,5}$/, 'Country Code must be numeric'),
    responsiblePersonTelephone: numericUpTo10DigitsRequiredField('Number'),
    // Removed from Return - made optional
    responsiblePersonSTDAlt: z.string().optional().nullable(),
    responsiblePersonTelephoneAlt: z.string().optional().nullable(),
    responsiblePersonAddressChanged: z.string().optional().nullable(),
    responsiblePersonMobile: z.string().optional().nullable(),

    responsiblePersonRegularStatement: z.enum(['Yes', 'No'], {
      errorMap: () => ({
        message:
          'Has the statement been filled earlier for this Quarter is required',
      }),
    }),
    responsiblePersonReceiptNumber: z
      .string()
      .trim()
      .optional()
      .refine(val => !val || /^[0-9]+$/.test(val), {
        message: 'Receipt No. must contain only numeric values',
      })
      .refine(val => !val || val.length <= 15, {
        message: 'Receipt No. must not exceed 15 digits',
      }),
  })
  .superRefine((data, ctx) => {
    // PAN validation - Entity PAN and Responsible Person PAN should not be same
    if (data.pan && data.pan === data.responsiblePersonPAN) {
      ctx.addIssue({
        path: ['pan'],
        code: z.ZodIssueCode.custom,
        message: 'PAN of deductor should not be same as responsible person PAN',
      })
    }

    // Telephone validation - Either both STD and telephone should be filled or both should be empty
    const hasStdCode = data.stdCode && data.stdCode.trim() !== ''
    const hasTelephoneNumber =
      data.telephoneNo && data.telephoneNo.trim() !== ''

    if (
      (hasStdCode && !hasTelephoneNumber) ||
      (!hasStdCode && hasTelephoneNumber)
    ) {
      ctx.addIssue({
        path: ['telephoneNo'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both Country Code & Number should be blank or both should be filled',
      })
    }

    // Responsible Person Telephone validation
    const hasStdCodeRP =
      data.responsiblePersonSTD && data.responsiblePersonSTD.trim() !== ''
    const hasTelephoneNumberRP =
      data.responsiblePersonTelephone &&
      data.responsiblePersonTelephone.trim() !== ''

    if (
      (hasStdCodeRP && !hasTelephoneNumberRP) ||
      (!hasStdCodeRP && hasTelephoneNumberRP)
    ) {
      ctx.addIssue({
        path: ['responsiblePersonTelephone'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both Country Code & Number should be blank or both should be filled',
      })
    }

    // Ministry Name Other validation - Required if ministryName is 'OTHERS'
    if (
      data.ministryName === 'OTHERS' &&
      (!data.ministryNameOther || data.ministryNameOther.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ministryNameOther'],
        code: z.ZodIssueCode.custom,
        message:
          'Ministry Name Other is required when Others is selected as Ministry Name',
      })
    }

    // Responsible Person Regular Statement — Receipt No validation
    if (data.responsiblePersonRegularStatement === 'Yes') {
      if (
        !data.responsiblePersonReceiptNumber ||
        data.responsiblePersonReceiptNumber.trim() === ''
      ) {
        ctx.addIssue({
          path: ['responsiblePersonReceiptNumber'],
          code: z.ZodIssueCode.custom,
          message:
            'Receipt Number of Original Return is required',
        })
      }
    }
  })

export const RETURN_COLUMN = [
  {
    id: 'entityName',
    label: 'Entity name',
    width: '150px',
  },
  {
    id: 'entityTan',
    label: 'Entity tan',
    width: '120px',
  },
  {
    id: 'financialYear',
    label: 'Tax year',
    width: '160px',
  },
  {
    id: 'quarter',
    label: 'Quarter',
    width: '160px',
    type: 'dropdown',
    options: [
      { id: 1, label: 'Q1', value: 'Q1' },
      { id: 2, label: 'Q2', value: 'Q2' },
      { id: 3, label: 'Q3', value: 'Q3' },
      { id: 4, label: 'Q4', value: 'Q4' },
    ],
  },
  {
    id: 'returnType',
    label: 'Return type',
    width: '160px',
    type: 'dropdown',
    options: [
      { id: 1, label: 'ORIGINAL', value: 'ORIGINAL' },
      { id: 2, label: 'REVISED', value: 'REVISED' },
    ],
  },
  {
    id: 'formType',
    label: 'Form',
    width: '160px',
    type: 'dropdown',
    options: [
      { id: 1, label: '26Q', value: '26Q' },
      { id: 2, label: '27Q', value: '27Q' },
      { id: 3, label: '140', value: '140' },
      { id: 4, label: '144', value: '144' },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    width: '150px',
    type: 'dropdown',
    options: [
      { id: 1, label: 'IN_PROGRESS', value: 'IN_PROGRESS' },
      { id: 2, label: 'COMPLETED', value: 'COMPLETED' },
    ],
    render: (value, row) => {
      const getStatusColor = status => {
        switch (status?.toUpperCase()) {
          case 'COMPLETED':
            return { backgroundColor: '#F0FDF3', color: '#197C37' }
          case 'FAILED':
          case 'DELETED':
            return { backgroundColor: '#FEF3F2', color: '#E0301E' }
          case 'IN PROGRESS':
            return { backgroundColor: '#FFDFC4', color: '#D17300' }
          default:
            return { backgroundColor: '#E2E3E5', color: '#495057' }
        }
      }

      const { backgroundColor, color } = getStatusColor(value)

      const badge = (
        <span
          style={{
            backgroundColor,
            color,
            padding: '2px 6px',
            borderRadius: 4,
            display: 'inline-block',
            cursor: 'pointer',
            alignContent: 'start'
          }}
        >
          {value || 'N/A'}
        </span>
      )

      if (value?.toUpperCase() === 'FAILED') {
        return (
          <WhiteTooltip placement="top" title={row?.errorMessage || ''} arrow>
            <span>{badge}</span>
          </WhiteTooltip>
        )
      } else {
        return (
          <WhiteTooltip placement="top" title={row?.statusMessage || ''} arrow>
            <span>{badge}</span>
          </WhiteTooltip>
        )
      }

      return badge
    },
  },
  {
    id: 'lastModifiedBy',
    label: 'Generated by',
    width: '160px',
  },
  {
    id: 'lastModifiedDate',
    label: 'Updated date',
    width: '160px',
  },
]

export const searchFields = [
  ...RETURN_COLUMN.map(col => {
    return {
      ...col,
      type: col.type || 'text',
      placeholder: `Enter ${col.label}`,
    }
  }),
]
